import { assertUnreachable, isNotDefined } from '@sgme/fp';
import { isBefore, parseISO } from 'date-fns';

import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { flexiForwardSlice } from '@/store/flexi-forward/slice';
import type { FlexiForwardRfsStatus, FxFlexiForward } from '@/store/flexi-forward/state';
import { createAppSelector } from '@/store/storeMethods';

export const getRequestFlexiForwardRfsMutationCacheKey = (tileId: string) => `flexi-forward-rfs-request-${tileId}`;

export function isExpiryDateValid(flexiForward: FxFlexiForward): boolean {
  const maxDate = flexiForward.properties.maxDate;
  if (isNotDefined(maxDate)) {
    return true;
  }

  const expiryDate = flexiForward.properties.expiryDate;
  return isNotDefined(expiryDate) || isBefore(parseISO(expiryDate), maxDate);
}

export function isPriceable(flexiForward: FxFlexiForward): boolean {
  return flexiForward.tradeCapture.status === 'CHECKED' && flexiForward.tradeCapture.isReadyToPrice;
}

export function selectFlexiForwardRfsStatus(flexiForward: FxFlexiForward): FlexiForwardRfsStatus {
  if (!isExpiryDateValid(flexiForward)) {
    return 'NOT_PRICEABLE';
  }

  const flexiForwardRfs = flexiForward.properties.rfs;

  if (isNotDefined(flexiForwardRfs?.status)) {
    return 'NOT_PRICEABLE';
  }

  switch (flexiForwardRfs?.status) {
    case 'EXPIRED':
    case 'CANCELLED':
      return isPriceable(flexiForward) ? 'PRICEABLE' : 'NOT_PRICEABLE';
    // TODO ABO, manage bypassable error status
    // const isBypassable = option.properties.rfs.error.multipassErrorCode === 'RrCreditCheckFailed'; // && sl.isUserInternalSales(state);
    // return isBypassable ? 'BYPASSABLE_ERROR' : 'ERROR';
    case 'ERROR':
      return 'ERROR';
    case 'AWAITING':
      return flexiForwardRfs.quoteType === 'Tradeable' ? 'AWAITING_EXECUTABLE' : 'AWAITING_INDICATIVE';
    case 'PRICING':
      return flexiForwardRfs.quoteType === 'Tradeable' ? 'STREAMING_EXECUTABLE' : 'STREAMING_INDICATIVE';

    default:
      return assertUnreachable(flexiForwardRfs, 'Unhandled stream status');
  }
}

export const selectFlexiForwardRfsPremiumData = createAppSelector(
  [
    (state, tileId) => flexiForwardSlice.selectors.getFlexiForwardTileProperties(state, tileId),
    (_state, tileId) => tileId,
  ],
  (properties) => {
    if (isNotDefined(properties)) {
      return undefined;
    }

    const premiumData = getFlexiForwardQuote(properties?.rfs)?.premiumData;

    if (isNotDefined(premiumData)) {
      return undefined;
    }

    return [
      { ask: premiumData.askAmountCcy1, bid: premiumData.bidAmountCcy1 },
      { ask: premiumData.askAmountCcy2, bid: premiumData.bidAmountCcy2 },
    ] as const;
  },
);
